import { useState } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Grid, TextField, Alert, MenuItem } from '@mui/material';
import AuthService from '../../services/AuthService';
import * as constantes from '../../common/Constantes';
import * as yup from "yup";
import Utilities from '../../common/Utilities';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ConfigUtils from '../../utils/Config';
import { CONFIGTYPES } from '../../types/Config';
import CambioTitularDialog, { mensajeDeError, ModoCambioTitular, pideCambioTitular } from './CambioTitularDialog';
import { CasoCambioTitular, ICambioTitularOpciones } from '../../types/EntidadFacturacion';

export interface UserBillingData {
    nombre: string,
    nif: string,
    calle: string,
    ciudad: string,
    codigoPostal: string,
    pais: string
}

type BillingDataFormProps = {
  userId: string;
  initialData: UserBillingData;
  onSuccess?: (updatedData: UserBillingData, caso: CasoCambioTitular) => void;
};

const schema = yup.object({
  nombre: yup.string().required('El nombre o razón social es obligatorio'),
  nif: yup.string()
    .required('El NIF es obligatorio')
    .test('nif-validation', 'El NIF no es válido', function (value) {
      const pais = this.parent.pais || 'ES';
      if (Utilities.validateTaxId(value || '', pais)) return true;
      return this.createError({ message: `${Utilities.getNifLabel(pais)} no válido` });
    }),
  calle: yup.string().required('La calle es obligatoria'),
  ciudad: yup.string().required('La ciudad es obligatoria'),
  codigoPostal: yup.string()
    .required('El código postal es obligatorio')
    .test('cp-validation', 'El código postal no tiene un formato válido', function (value) {
      return Utilities.validatePostalCode(value || '', this.parent.pais || 'ES');
    }),
  pais: yup.string().required('El país es obligatorio')
});

const BillingDataForm = (props: BillingDataFormProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cambio de NIF que el servidor no aplica sin más (cambio de titular): se pregunta y se reintenta con la respuesta
  const [cambioTitular, setCambioTitular] = useState<{ modo: ModoCambioTitular; mensaje: string; data: UserBillingData } | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<UserBillingData>({
    defaultValues: props.initialData,
    resolver: yupResolver(schema)
  });

  const pais = watch('pais');
  const nifLabel = Utilities.getNifLabel(pais);

  // El servidor decide si un NIF nuevo es un intercambio con una secundaria o un cambio de titular con serie nueva
  // (y responde 409 para preguntarlo); `opciones` lleva la respuesta del usuario en el reintento.
  const guardarDatos = async (data: UserBillingData, opciones: ICambioTitularOpciones = {}) => {
    const response = await AuthService.updateBillingData(props.userId, { ...data, ...opciones });
    const { billingData, datosFacturacionAdicional, caso } = response.data;
    ConfigUtils.addConfig(CONFIGTYPES.datosFacturacion.toString(), billingData);
    ConfigUtils.setDatosFacturacionAdicional(datosFacturacionAdicional);
    setCambioTitular(null);
    props.onSuccess?.(billingData, caso);
  };

  const onSubmit = async (data: UserBillingData) => {
    setLoading(true);
    setError(null);
    try {
      await guardarDatos(data);
    } catch (err: unknown) {
      const pregunta = pideCambioTitular(err);
      if (pregunta) setCambioTitular({ ...pregunta, data });
      else setError(mensajeDeError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Grid item xs={12}>
      <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ maxWidth: 600, margin: "0 auto", paddingBottom: 2 }}>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="nombre"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Nombre o razón social"
                        fullWidth
                        error={!!errors.nombre}
                        helperText={errors.nombre?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="nif"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={nifLabel}
                        fullWidth
                        error={!!errors.nif}
                        helperText={errors.nif?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="calle"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Calle / Dirección"
                        fullWidth
                        error={!!errors.calle}
                        helperText={errors.calle?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="ciudad"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Ciudad"
                        fullWidth
                        error={!!errors.ciudad}
                        helperText={errors.ciudad?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="codigoPostal"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Código postal"
                        fullWidth
                        error={!!errors.codigoPostal}
                        helperText={errors.codigoPostal?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="pais"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="País"
                        fullWidth
                        error={!!errors.pais}
                        helperText={errors.pais?.message}
                      >
                        {constantes.paises.map((pais: any) => (
                          <MenuItem key={pais.id} value={pais.id}>
                            {pais.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                color="success"
                fullWidth
                sx={{
                  mt: 3,
                  py: 1.2,
                  fontWeight: 'bold',
                }}
                onClick={handleSubmit(onSubmit)}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Editar datos de facturación'}
              </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {cambioTitular && (
      <CambioTitularDialog
        open
        onClose={() => setCambioTitular(null)}
        modo={cambioTitular.modo}
        mensaje={cambioTitular.mensaje}
        pais={cambioTitular.data.pais}
        onConfirmar={(opciones) => guardarDatos(cambioTitular.data, opciones)}
      />
    )}
    </>
  );
};

export default BillingDataForm;
